import { HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, Optional, NgModule } from '@angular/core';
import { OTEL_CONFIG, OTEL_EXPORTER, OTEL_PROPAGATOR, OTEL_LOGGER, OTEL_CUSTOM_SPAN, OpenTelemetryInterceptorModule, OtelColExporterModule, CompositePropagatorModule } from '@jufab/opentelemetry-angular-interceptor';
import { tap, finalize } from 'rxjs/operators';
import { SpanStatusCode, SpanKind, trace, propagation, defaultTextMapSetter } from '@opentelemetry/api';
import { isUrlIgnored } from '@opentelemetry/core';
import { Resource } from '@opentelemetry/resources';
import { SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider, StackContextManager } from '@opentelemetry/sdk-trace-web';
import { SemanticResourceAttributes, SemanticAttributes } from '@opentelemetry/semantic-conventions';
import * as i1 from '@angular/common';
import * as i2 from '@mercadona/core/platform';

var MTraces;
(function (MTraces) {
    /** Enables the option to trace all the calls made */
    MTraces[MTraces["ALL"] = 2] = "ALL";
    /** Disables trace generation for any type of call */
    MTraces[MTraces["OFF"] = 1] = "OFF";
    /**
     * Executes the default scenario, when a trace of a call is launched,
     * if the same call is executed again, it does not launch a new trace,
     * a filter is performed
     */
    MTraces[MTraces["DEFAULT"] = 0] = "DEFAULT";
})(MTraces || (MTraces = {}));

/**
 * OpenTelemetryInterceptor class
 */
class MTelemetryInterceptor {
    /**
     * constructor
     *
     * @param {MTelemetryPrivateConfig} config configuration
     * @param {IExporter} exporterService service exporter injected
     * @param {IPropagator} propagatorService propagator injected
     * @param {PlatformLocation} platformLocation encapsulates all calls to DOM APIs
     * @param {MPlatformService} mPlatformService service to get project information, such as name, version...
     * @param {DiagLogger} logger define logger
     * @param {CustomSpan} customSpan a customSpan interface to add attributes
     */
    constructor(config, exporterService, propagatorService, platformLocation, mPlatformService, logger, customSpan) {
        this.config = config;
        this.exporterService = exporterService;
        this.propagatorService = propagatorService;
        this.platformLocation = platformLocation;
        this.mPlatformService = mPlatformService;
        this.logger = logger;
        this.customSpan = customSpan;
        /**
         * Save cache urls
         */
        this.requestCache = new Set();
        /**
         * Default ignore urls
         */
        this.defaultIgnoreUrls = [new RegExp(/googleapis/)];
        this.configDefault = {
            commonConfig: {
                serviceName: this.mPlatformService.appName,
                traces: this.config.commonConfig.traces
            }
        };
        this.tracer = new WebTracerProvider({
            resource: Resource.default().merge(new Resource({
                [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: this.mPlatformService.environment,
                [SemanticResourceAttributes.SERVICE_NAME]: this.configDefault.commonConfig?.serviceName
            }))
        });
        this.config = {
            ...this.config,
            ignoreUrls: {
                urls: this.config.ignoreUrls?.urls
                    ? this.config.ignoreUrls?.urls?.concat(this.defaultIgnoreUrls)
                    : this.defaultIgnoreUrls
            }
        };
        this.insertOrNotSpanExporter();
        this.contextManager = new StackContextManager();
        this.tracer.register({
            propagator: this.propagatorService.getPropagator(),
            contextManager: this.contextManager
        });
    }
    /**
     * Overide method
     * Interceptor from HttpInterceptor Angular
     *
     * @param {HttpRequest<any>} request the current request
     * @param {HttpHandler} next next
     * @returns {Observable<HttpEvent<any>>} observable that intercept
     */
    intercept(request, next) {
        let urlToCompare = '';
        const regex = new RegExp('/[A-Za-z1-9/-]+/[^1-9/]+', 'g');
        const regexResults = request.url.match(regex);
        if (regexResults) {
            urlToCompare = regexResults[0];
        }
        if (this.config.commonConfig.traces === MTraces.OFF ||
            this.requestCache.has(urlToCompare) ||
            isUrlIgnored(request.url, this.config.ignoreUrls?.urls)) {
            return next.handle(request);
        }
        this.contextManager.disable();
        this.contextManager.enable();
        const span = this.initSpan(request);
        const tracedReq = this.injectContextAndHeader(request);
        if (this.config.commonConfig.traces === MTraces.DEFAULT) {
            this.requestCache.add(urlToCompare);
        }
        return next.handle(tracedReq).pipe(tap({
            next: (event) => {
                span.setAttributes({
                    [SemanticAttributes.HTTP_STATUS_CODE]: event.status
                });
                span.setStatus({
                    code: SpanStatusCode.UNSET
                });
                this.setCustomSpan(span, request, event);
            }
        }), tap({
            error: (event) => {
                span.setAttributes({
                    [SemanticAttributes.HTTP_STATUS_CODE]: event.status
                });
                span.recordException({
                    name: event.name,
                    message: event.message,
                    stack: event.error
                });
                span.setStatus({
                    code: SpanStatusCode.ERROR
                });
                this.setCustomSpan(span, request, event);
            }
        }), finalize(() => {
            span.end();
            this.contextManager.disable();
        }));
    }
    /**
     * Get current scheme, hostname and port
     *
     * @returns {string} current scheme
     */
    getURL() {
        return this.platformLocation.href;
    }
    /**
     * Initialise a span for a request intercepted
     *
     * @param {HttpRequest<unknown>} request request
     * @returns {Span} initial span
     */
    initSpan(request) {
        const urlRequest = request.urlWithParams.startsWith('http')
            ? new URL(request.urlWithParams)
            : new URL(this.getURL());
        const span = this.tracer.getTracer(this.mPlatformService.appName, this.mPlatformService.appVersion).startSpan(`${urlRequest.protocol.replace(':', '').toUpperCase()} ${request.method.toUpperCase()}`, {
            attributes: {
                [SemanticAttributes.HTTP_METHOD]: request.method,
                [SemanticAttributes.HTTP_URL]: request.urlWithParams,
                [SemanticAttributes.HTTP_HOST]: urlRequest.host,
                [SemanticAttributes.HTTP_SCHEME]: urlRequest.protocol.replace(':', ''),
                [SemanticAttributes.HTTP_TARGET]: urlRequest.pathname + urlRequest.search,
                [SemanticAttributes.HTTP_USER_AGENT]: window.navigator.userAgent
            },
            kind: SpanKind.CLIENT
        }, this.contextManager.active());
        this.contextManager._currentContext = trace.setSpan(this.contextManager.active(), span);
        return span;
    }
    /**
     * Add header propagator in request and conserve original header
     *
     * @param {HttpRequest<unknown>} request request
     * @returns {HttpRequest<unknown>} resquets modified
     */
    injectContextAndHeader(request) {
        const carrier = {};
        propagation.inject(this.contextManager.active(), carrier, defaultTextMapSetter);
        request.headers.keys().map((key) => {
            carrier[key] = request.headers.get(key);
        });
        return request.clone({
            setHeaders: carrier
        });
    }
    /**
     * Verify to insert or not a Span Exporter
     */
    insertOrNotSpanExporter() {
        if (this.exporterService.getExporter() !== undefined) {
            this.tracer.addSpanProcessor(new SimpleSpanProcessor(this.exporterService.getExporter()));
        }
    }
    /**
     * Set custom attributes in span with a CustomSpan
     *
     * @param {Span} span span
     * @param {HttpRequest<unknown>} request request
     * @param {HttpResponse<unknown> | HttpErrorResponse} response response
     * @returns {Span} span custom
     */
    setCustomSpan(span, request, response) {
        return this.customSpan !== null ? this.customSpan.add(span, request, response) : span;
    }
}
MTelemetryInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MTelemetryInterceptor, deps: [{ token: OTEL_CONFIG }, { token: OTEL_EXPORTER }, { token: OTEL_PROPAGATOR }, { token: i1.PlatformLocation }, { token: i2.MPlatformService }, { token: OTEL_LOGGER, optional: true }, { token: OTEL_CUSTOM_SPAN, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
MTelemetryInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MTelemetryInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MTelemetryInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [OTEL_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [OTEL_EXPORTER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [OTEL_PROPAGATOR]
                }] }, { type: i1.PlatformLocation }, { type: i2.MPlatformService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [OTEL_LOGGER]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [OTEL_CUSTOM_SPAN]
                }] }]; } });

class MTelemetryModule {
    static forRoot(moduleConfig) {
        return {
            ngModule: MTelemetryModule,
            providers: [
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: MTelemetryInterceptor,
                    multi: true
                },
                {
                    provide: OTEL_CONFIG,
                    useValue: {
                        commonConfig: {
                            traces: moduleConfig.traces
                        },
                        otelcolConfig: {
                            url: moduleConfig.url
                        },
                        ignoreUrls: {
                            urls: moduleConfig.ignoreUrls
                        }
                    }
                }
            ]
        };
    }
}
MTelemetryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MTelemetryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MTelemetryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MTelemetryModule, imports: [OpenTelemetryInterceptorModule, OtelColExporterModule, CompositePropagatorModule] });
MTelemetryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MTelemetryModule, imports: [OpenTelemetryInterceptorModule, OtelColExporterModule, CompositePropagatorModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MTelemetryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [OpenTelemetryInterceptorModule, OtelColExporterModule, CompositePropagatorModule]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MTelemetryModule, MTraces };
